import { defineStore } from 'pinia';
import { usePostMessage } from '@/composables/usePostMessage';

/** Prix mini d'un produit (ou d'une méthode) : montant formaté + quantité associée. */
export interface FromPrice {
    /** Prix formaté prêt à l'affichage (ex. "26,70€"), fourni par l'hôte. */
    price: string;
    /** Quantité (unités) à laquelle ce prix mini s'applique. */
    qty: number;
}

export interface CatalogProductPrice {
    /** Prix mini toutes méthodes confondues (pour la card « à partir de »). */
    from: FromPrice;
    /** Prix mini par méthode d'impression (clé = print_profile_id C3D). */
    byMethod?: Record<string, FromPrice>;
    /**
     * variantRef Medusa du BON produit lié à ce produit C3D (fourni par l'hôte).
     * Permet d'OUVRIR le configurateur sur la bonne variante en mode catalogue
     * (via /variants/{ref}) au lieu de laisser C3D deviner « le mapping le plus
     * récent » (qui peut pointer sur un autre produit Medusa). Optionnel.
     */
    variantRef?: string;
}

/**
 * PRIX DU TUNNEL (mode e-commerce uniquement). Le FO ne peut pas pricer un produit
 * catalogue sans l'ouvrir NI sans le mapping variante→méthode (qui vit côté hôte) :
 * on demande donc à l'hôte, en UN message, les prix mini d'une liste de produits
 * (`askCatalogPrices` → `sendCatalogPrices`). Cf. usePostMessage.
 *
 * Convention de valeur par produit :
 *  - `undefined` (clé absente) : pas encore connu (requête en vol / non demandé) ;
 *  - `null` : l'hôte a répondu « pas de prix » → produit MASQUÉ du catalogue ;
 *  - objet : prix connu → affiché.
 *
 * En STANDALONE (pas embarqué), le pricing est inactif : aucun prix, aucun masquage.
 */
export const useCatalogPricingStore = defineStore('catalogPricing', {
    state: () => ({
        // Embarqué dans une page hôte → il y a un parent qui peut pricer.
        enabled: typeof window !== 'undefined' && window.parent !== window,
        prices: {} as Record<string, CatalogProductPrice | null>,
        // Produits déjà demandés (évite les requêtes en double).
        requested: new Set<string>(),
        // Une réponse hôte a été reçue au moins une fois (sinon → fallback legacy).
        answered: false
    }),
    getters: {
        /** Prix connu d'un produit (objet), `null` (pas de prix), ou `undefined` (inconnu). */
        priceFor:
            (state) =>
            (productId: string): CatalogProductPrice | null | undefined =>
                state.prices[productId],
        /** variantRef du bon produit Medusa pour ce produit C3D (si l'hôte l'a fourni). */
        variantRefFor:
            (state) =>
            (productId: string): string | undefined =>
                state.prices[productId]?.variantRef,
        /**
         * Le produit doit-il être MASQUÉ ? Uniquement en e-commerce, et seulement
         * quand l'hôte a explicitement répondu « pas de prix » (null). Tant que
         * c'est inconnu (undefined), on l'affiche (pas de flash de catalogue vide).
         */
        isHidden:
            (state) =>
            (productId: string): boolean =>
                state.enabled && state.prices[productId] === null
    },
    actions: {
        /** Demande à l'hôte les prix des produits pas encore demandés (mode e-commerce). */
        requestPrices(productIds: string[]) {
            if (!this.enabled) {
                return;
            }
            const missing = productIds.filter((id) => id && !this.requested.has(id));
            if (!missing.length) {
                return;
            }
            missing.forEach((id) => this.requested.add(id));
            usePostMessage().askCatalogPrices(missing);
        },
        /** Applique une réponse `sendCatalogPrices` de l'hôte. */
        applyHostPrices(map: Record<string, CatalogProductPrice | null>) {
            if (!map || typeof map !== 'object') {
                return;
            }
            this.answered = true;
            for (const [id, value] of Object.entries(map)) {
                this.prices[id] = value ?? null;
            }
        }
    }
});
